import { useMemo } from 'react';
import { useBalance } from '@/queries/useBalance';
import { useUser } from '@/queries/useUser';
import { formatCurrency } from '@/lib/currency';
import { StanModal } from '@/components';
import { CashOutTimeLineDateRow } from './CashOutTimeLineDateRow';

interface CashOutTimelineModalProps {
    isOpen: boolean;
    onClose: () => void;
    availableSoonAmount: string;
}
export function CashOutTimelineModal({ isOpen, onClose, availableSoonAmount }: CashOutTimelineModalProps) {
    const { balance } = useBalance();
    const { user } = useUser();

    const stripeDisputedAmount = useMemo(() => {
        if (balance?.stripe_dispute_hold?.amount === 0) {
            return 0;
        }
        return balance?.stripe_dispute_hold?.amount || 0;
    }, [balance]);

    const stripeDisputedCurrency = balance?.stripe_dispute_hold?.currency || 'USD';

    const cashOutTimelineDataList = useMemo(() => {
        const userSelectedCurrency = user?.data?.currency || 'USD';

        return [
            {
                title: 'Processing Purchases',
                amount: formatCurrency(
                    balance?.stripe_pending?.amount || 0,
                    balance?.stripe_pending?.currency || userSelectedCurrency
                ),
            },
            {
                title: `Referral Bonus ${userSelectedCurrency !== 'USD' ? '(USD)' : ''}`,
                amount: formatCurrency(
                    balance?.referral_balance?.amount || 0,
                    balance?.referral_balance?.currency || userSelectedCurrency
                ),
            },
            {
                title: `Affiliate Bonus ${userSelectedCurrency !== 'USD' ? '(USD)' : ''}`,
                amount: formatCurrency(
                    balance?.affiliate_balance?.amount || 0,
                    balance?.affiliate_balance?.currency || userSelectedCurrency
                ),
            },
        ];
    }, [balance, user?.data?.currency]);

    return (
        <StanModal
            id='cash-out-timeline-modal'
            isOpen={isOpen}
            onClose={onClose}
            title='Earnings Breakdown'
            primaryActionLabel='Okay'
            onPrimaryAction={onClose}
        >
            <div className='flex w-full flex-col gap-6'>
                <div className='flex flex-col text-center'>
                    <p className='text-gray text-caption-1-bold'>See how your earnings are split up on Stan.</p>
                </div>

                <div className='flex w-full flex-col gap-4'>
                    <div className='flex w-full flex-col gap-4 px-6 pt-10'>
                        <div className='flex items-center justify-between'>
                            <div className='text-gray text-caption-1-bold'>Available Soon</div>
                            <h2 className='font-inter font-semibold'>{availableSoonAmount}</h2>
                        </div>

                        {cashOutTimelineDataList.map(row => (
                            <CashOutTimeLineDateRow key={row.title} title={row.title} amount={row.amount} />
                        ))}
                    </div>

                    {stripeDisputedAmount > 0 && (
                        <div className='flex w-full flex-col gap-6'>
                            <div className='text-gray bg-neutral-subtle flex w-full items-center justify-between rounded-md px-4 py-2.5'>
                                <div className='text-gray text-caption-2-bold'>Active Stripe Disputes</div>
                                <span className='text-caption-1-bold'>
                                    {formatCurrency(stripeDisputedAmount, stripeDisputedCurrency)}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </StanModal>
    );
}
